import { useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SerializedEditorState } from 'lexical';
import { useParams } from 'react-router';

import {
  getCurriculumOptions,
  getCurriculumQueryKey,
  publishCurriculumMutation,
  updateCurriculumMutation,
} from '@drivingdoc/api-client/generated/react-query';
import type { Curriculum, CurriculumDocument, CurriculumLesson, CurriculumQuiz } from '@drivingdoc/api-client/generated/types';

import { BasicRichTextEditor } from 'components/Editor/BasicRichTextEditor';
import { PageContainer } from 'components/PageContainer';

import QuizEditor from './QuizEditor';

const emptyEditorState = {
  root: { children: [], direction: null, format: '', indent: 0, type: 'root', version: 1 },
} as unknown as SerializedEditorState;

type CurriculumItem = CurriculumLesson | CurriculumQuiz;

function readS3Key(content: Record<string, unknown>): string {
  return typeof content.s3Key === 'string' ? content.s3Key : '';
}

function lessonHasRichText(content: Record<string, unknown>): boolean {
  const root = content.root;
  if (!root || typeof root !== 'object') return false;
  const children = (root as { children?: unknown[] }).children;
  return Array.isArray(children) && children.length > 0;
}

function withLessonContent(
  content: Record<string, unknown>,
  changes: { s3Key?: string; editorState?: SerializedEditorState },
): Record<string, unknown> {
  const next: Record<string, unknown> = changes.editorState
    ? { ...(changes.editorState as unknown as Record<string, unknown>) }
    : { ...content };
  const s3Key = changes.s3Key !== undefined ? changes.s3Key.trim() : readS3Key(content);
  delete next.videoUrl;
  if (s3Key) next.s3Key = s3Key;
  else delete next.s3Key;
  delete next.s3Bucket;
  return next;
}

function LessonEditor({
  item,
  onChange,
  onDelete,
}: {
  item: CurriculumLesson;
  onChange: (lesson: CurriculumLesson) => void;
  onDelete: () => void;
}) {
  const initialEditorState = useRef(
    (item.content.root ? item.content : emptyEditorState) as unknown as SerializedEditorState,
  );
  const s3Key = readS3Key(item.content);
  const [showNotes, setShowNotes] = useState(() => !s3Key || lessonHasRichText(item.content));

  return (
    <Stack spacing={1}>
      <Stack direction="row">
        <TextField
          label="Lesson title"
          value={item.title}
          onChange={(event) => onChange({ ...item, title: event.target.value })}
          fullWidth
        />
        <IconButton aria-label="Delete lesson" onClick={onDelete}>
          <DeleteIcon />
        </IconButton>
      </Stack>
      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          label="Minimum seconds"
          type="number"
          value={item.minTimeSeconds}
          onChange={(event) => onChange({ ...item, minTimeSeconds: Number(event.target.value) })}
        />
        <FormControlLabel
          label="Required"
          control={
            <Checkbox
              checked={item.required}
              onChange={(event) => onChange({ ...item, required: event.target.checked })}
            />
          }
        />
      </Stack>
      <TextField
        label="S3 object key"
        helperText="Private object key in the learning media bucket (e.g. Chapter1/lesson-1.mp4)"
        value={s3Key}
        onChange={(event) =>
          onChange({
            ...item,
            content: withLessonContent(item.content, { s3Key: event.target.value }),
          })
        }
        fullWidth
      />
      {showNotes ? (
        <BasicRichTextEditor
          namespace={`curriculum-${item.id}`}
          initialState={initialEditorState.current}
          onChange={(content: SerializedEditorState) =>
            onChange({
              ...item,
              content: withLessonContent(item.content, { editorState: content }),
            })
          }
        />
      ) : (
        <Button size="small" onClick={() => setShowNotes(true)}>
          Edit rich text notes
        </Button>
      )}
    </Stack>
  );
}

/**
 * Edits live in local state so keystrokes do not rewrite the full curriculum draft
 * on every input. Pending edits are read via flushRef before save / selection change.
 */
function SelectedItemEditor({
  item,
  onDelete,
  pendingItemRef,
}: {
  item: CurriculumItem;
  onDelete: () => void;
  pendingItemRef: MutableRefObject<CurriculumItem | null>;
}) {
  const [local, setLocal] = useState(item);

  useEffect(() => {
    setLocal(item);
    pendingItemRef.current = item;
  }, [item.id, pendingItemRef]);

  useEffect(() => {
    pendingItemRef.current = local;
  }, [local, pendingItemRef]);

  if (local.type === 'quiz') {
    return <QuizEditor quiz={local} onChange={setLocal} onDelete={onDelete} />;
  }
  return <LessonEditor item={local} onChange={setLocal} onDelete={onDelete} />;
}

export default function CurriculumEditorPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const query = useQuery({ ...getCurriculumOptions({ path: { id } }), enabled: Boolean(id) });
  const update = useMutation(updateCurriculumMutation());
  const publish = useMutation(publishCurriculumMutation());
  const [draft, setDraft] = useState<Curriculum | null>(null);
  const [message, setMessage] = useState('');
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const pendingItemRef = useRef<CurriculumItem | null>(null);

  useEffect(() => {
    if (query.data) setDraft(query.data);
  }, [query.data]);

  const itemCounts = useMemo(() => {
    if (!draft) return { lessons: 0, quizzes: 0, questions: 0 };
    let lessons = 0;
    let quizzes = 0;
    let questions = 0;
    for (const module of draft.draft.modules) {
      for (const item of module.items) {
        if (item.type === 'lesson') lessons += 1;
        else {
          quizzes += 1;
          questions += item.questions.length;
        }
      }
    }
    return { lessons, quizzes, questions };
  }, [draft]);

  const selected = useMemo(() => {
    if (!draft || !selectedItemId) return null;
    for (let moduleIndex = 0; moduleIndex < draft.draft.modules.length; moduleIndex += 1) {
      const module = draft.draft.modules[moduleIndex];
      const itemIndex = module.items.findIndex((entry) => entry.id === selectedItemId);
      if (itemIndex >= 0) {
        return { moduleIndex, itemIndex, item: module.items[itemIndex], moduleTitle: module.title };
      }
    }
    return null;
  }, [draft, selectedItemId]);

  if (!draft) return <PageContainer title="Curriculum">Loading…</PageContainer>;

  const withPendingItem = (document: CurriculumDocument): CurriculumDocument => {
    const pending = pendingItemRef.current;
    if (!pending || !selectedItemId) return document;
    return {
      ...document,
      modules: document.modules.map((module) => ({
        ...module,
        items: module.items.map((entry) => (entry.id === pending.id ? pending : entry)),
      })),
    };
  };

  const updateDocument = (document: CurriculumDocument) => setDraft({ ...draft, draft: document });
  const addModule = () => {
    const document = withPendingItem(draft.draft);
    updateDocument({
      ...document,
      modules: [...document.modules, { id: crypto.randomUUID(), title: 'New module', items: [] }],
    });
  };
  const updateModule = (moduleIndex: number, changes: Partial<CurriculumDocument['modules'][number]>) => {
    const document = withPendingItem(draft.draft);
    const modules = [...document.modules];
    modules[moduleIndex] = { ...modules[moduleIndex], ...changes };
    updateDocument({ ...document, modules });
  };
  const selectItem = (itemId: string | null) => {
    setDraft((current) => (current ? { ...current, draft: withPendingItem(current.draft) } : current));
    pendingItemRef.current = null;
    setSelectedItemId(itemId);
  };
  const removeSelected = () => {
    if (!selected) return;
    const {moduleIndex, itemIndex} = selected;
    pendingItemRef.current = null;
    setSelectedItemId(null);
    setDraft((current) => {
      if (!current) return current;
      const modules = [...current.draft.modules];
      modules[moduleIndex] = {
        ...modules[moduleIndex],
        items: modules[moduleIndex].items.filter((_, index) => index !== itemIndex),
      };
      return {...current, draft: {...current.draft, modules}};
    });
  };
  const addLesson = (moduleIndex: number) => {
    const lesson: CurriculumLesson = {
      id: crypto.randomUUID(),
      type: 'lesson',
      title: 'New lesson',
      required: true,
      minTimeSeconds: 0,
      content: emptyEditorState as unknown as Record<string, unknown>,
    };
    setDraft((current) => {
      if (!current) return current;
      const document = withPendingItem(current.draft);
      const modules = [...document.modules];
      modules[moduleIndex] = {
        ...modules[moduleIndex],
        items: [...modules[moduleIndex].items, lesson],
      };
      return {...current, draft: {...document, modules}};
    });
    pendingItemRef.current = null;
    setSelectedItemId(lesson.id);
  };
  const addQuiz = (moduleIndex: number) => {
    const quiz: CurriculumQuiz = {
      id: crypto.randomUUID(),
      type: 'quiz',
      title: 'New quiz',
      required: true,
      passingScorePercent: 80,
      minTimeSeconds: 60,
      questions: [
        {
          id: crypto.randomUUID(),
          type: 'multiple_choice',
          prompt: 'New question',
          options: [
            {id: crypto.randomUUID(), label: 'Option 1', isCorrect: true},
            {id: crypto.randomUUID(), label: 'Option 2', isCorrect: false},
          ],
        },
      ],
    };
    setDraft((current) => {
      if (!current) return current;
      const document = withPendingItem(current.draft);
      const modules = [...document.modules];
      modules[moduleIndex] = {
        ...modules[moduleIndex],
        items: [...modules[moduleIndex].items, quiz],
      };
      return {...current, draft: {...document, modules}};
    });
    pendingItemRef.current = null;
    setSelectedItemId(quiz.id);
  };
  const saveDraft = async () => {
    const document = withPendingItem(draft.draft);
    const saved = await update.mutateAsync({
      path: { id },
      body: {
        title: draft.title,
        description: draft.description,
        stateCode: draft.stateCode,
        revision: draft.revision,
        draft: document,
      },
    });
    setDraft(saved);
    await queryClient.invalidateQueries({ queryKey: getCurriculumQueryKey({ path: { id } }) });
    return saved;
  };
  const save = async () => {
    await saveDraft();
    setMessage('Saved.');
  };
  const publishNow = async () => {
    await saveDraft();
    const result = await publish.mutateAsync({ path: { id } });
    setMessage(`Saved and published version ${result.version}.`);
  };

  return (
    <PageContainer title={draft.title}>
      <Stack spacing={2}>
        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          alignItems="center"
          sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper', py: 1 }}
        >
          <Button variant="contained" onClick={save} disabled={update.isPending || publish.isPending}>
            Save
          </Button>
          <Button variant="outlined" onClick={publishNow} disabled={update.isPending || publish.isPending}>
            Save and publish
          </Button>
        </Stack>
        {(update.isError || publish.isError) && (
          <Alert severity="error">
            {update.error?.response?.status === 409
              ? 'Someone else updated this curriculum. Refresh before saving again.'
              : 'The curriculum could not be saved or published.'}
          </Alert>
        )}
        {message && <Alert severity="success">{message}</Alert>}
        <Typography variant="body2" color="text.secondary">
          {itemCounts.lessons} lessons · {itemCounts.quizzes} quizzes · {itemCounts.questions} questions — select one
          item to edit.
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
          <TextField
            label="Title"
            value={draft.title}
            onChange={(event) => setDraft({ ...draft, title: event.target.value })}
            fullWidth
          />
          <TextField
            label="State"
            value={draft.stateCode ?? ''}
            onChange={(event) => setDraft({ ...draft, stateCode: event.target.value || null })}
            inputProps={{ maxLength: 2 }}
          />
        </Stack>
        <TextField
          label="Description"
          value={draft.description ?? ''}
          onChange={(event) => setDraft({ ...draft, description: event.target.value || null })}
          multiline
        />
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
          <Stack spacing={2} sx={{ width: { xs: '100%', md: 360 }, flexShrink: 0 }}>
            {draft.draft.modules.map((module, moduleIndex) => (
              <Card key={module.id} variant="outlined">
                <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                  <Stack spacing={1}>
                    <Stack direction="row" alignItems="center">
                      <TextField
                        label="Module title"
                        value={module.title}
                        onChange={(event) => updateModule(moduleIndex, { title: event.target.value })}
                        fullWidth
                        size="small"
                      />
                      <IconButton
                        aria-label="Delete module"
                        onClick={() =>
                          updateDocument({
                            ...draft.draft,
                            modules: draft.draft.modules.filter((_, index) => index !== moduleIndex),
                          })
                        }
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                    <List dense disablePadding>
                      {module.items.map((item) => {
                        const secondary =
                          item.type === 'quiz'
                            ? `${item.questions.length} questions · ${item.passingScorePercent}%`
                            : readS3Key(item.content) || 'Rich text';
                        return (
                          <ListItemButton
                            key={item.id}
                            selected={selectedItemId === item.id}
                            onClick={() => selectItem(item.id)}
                          >
                            <Chip
                              size="small"
                              label={item.type === 'quiz' ? 'Quiz' : 'Lesson'}
                              sx={{ mr: 1 }}
                            />
                            <ListItemText
                              primary={item.title}
                              secondary={secondary}
                              primaryTypographyProps={{ noWrap: true }}
                              secondaryTypographyProps={{ noWrap: true }}
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" startIcon={<AddIcon />} onClick={() => addLesson(moduleIndex)}>
                        Add lesson
                      </Button>
                      <Button size="small" startIcon={<AddIcon />} onClick={() => addQuiz(moduleIndex)}>
                        Add quiz
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}
            <Button startIcon={<AddIcon />} onClick={addModule}>
              Add module
            </Button>
          </Stack>
          <Card variant="outlined" sx={{ flex: 1, width: '100%', minHeight: 240 }}>
            <CardContent>
              {!selected ? (
                <Typography color="text.secondary">Select a lesson or quiz from the list to edit it.</Typography>
              ) : (
                <Stack spacing={2}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {selected.moduleTitle}
                  </Typography>
                  <SelectedItemEditor
                    key={selected.item.id}
                    item={selected.item}
                    onDelete={removeSelected}
                    pendingItemRef={pendingItemRef}
                  />
                </Stack>
              )}
            </CardContent>
          </Card>
        </Stack>
      </Stack>
    </PageContainer>
  );
}
